import _ from 'lodash'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { handleOfflineError } from 'utils/apiFetch'
import { captureFrontendError } from 'utils/captureFrontendError'
import { checkAuthorization } from 'utils/checkAuthorization'
import { serviceCenterStore } from '../store/serviceCenterStore'
import { TServiceIssueStatus } from '../types'
import { serviceCenterActiveNodesGet } from './api/serviceCenterActiveNodesGet'
import { serviceCenterDevicesGet } from './api/serviceCenterDevicesGet'
import { serviceCenterLuresGet } from './api/serviceCenterLuresGet'
import { serviceCenterNodeLogsGet } from './api/serviceCenterNodeLogsGet'
import { serviceCenterNodeRemoveReasonsGet } from './api/serviceCenterNodeRemoveReasonsGet'
import { serviceCenterNodeServiceIssuesGet } from './api/serviceCenterNodeServiceIssuesGet'
import { serviceCenterNodeSwapReasonsGet } from './api/serviceCenterNodeSwapReasonsGet'
import { serviceCenterPlannedNodesGet } from './api/serviceCenterPlannedNodesGet'

const loadPlannedNodes = async (propertyIds: number[]) => {
  await handleOfflineError(async () => {
    const plannedNodes = await serviceCenterPlannedNodesGet(propertyIds)

    serviceCenterStore.actions.refreshPlannedNodesForProperties(plannedNodes, propertyIds)
  })
}

const loadActiveNodes = async (propertyIds: number[]) => {
  await handleOfflineError(async () => {
    const activeNodes = await serviceCenterActiveNodesGet(propertyIds)

    serviceCenterStore.actions.refreshActiveNodesForProperties(activeNodes, propertyIds)
  })
}

const loadNodeLogs = async (propertyIds: number[]) => {
  await handleOfflineError(async () => {
    if (checkAuthorization({ permission: 'VIEW_SSC_NODE_LAST_LOG', entity: 'FOR_ANY_PROPERTY' })) {
      const nodeLogs = await serviceCenterNodeLogsGet(propertyIds)

      serviceCenterStore.actions.refreshNodeLogsForProperties(nodeLogs)
    }
  })
}

const loadServiceIssues = async (propertyIds: number[]) => {
  await handleOfflineError(async () => {
    const serviceIssues = await serviceCenterNodeServiceIssuesGet({
      propertyIds,
      issueStatuses: [TServiceIssueStatus.CREATED, TServiceIssueStatus.FLAGGED_FOR_SERVICE],
    })

    serviceCenterStore.actions.refreshServiceIssuesForProperties(serviceIssues, propertyIds)
  })
}

const loadDevices = async () => {
  await handleOfflineError(async () => {
    if (checkAuthorization({ permission: 'VIEW_SSC_INSTALLABLE_DEVICES', entity: '*' })) {
      const devices = await serviceCenterDevicesGet()

      fieldAssetStore.setState((s) => ({ ...s, devices }))
    }
  })
}

const loadLures = async () => {
  await handleOfflineError(async () => {
    if (checkAuthorization({ permission: 'VIEW_SSC_LURES', entity: '*' })) {
      // a trap can still hold a lure that was deactivated after it was installed, so the lookup needs those too
      // (the lure pickers filter them out in getLuresAsArray)
      const lures = await serviceCenterLuresGet(true)

      serviceCenterStore.actions.setLures(lures)
    }
  })
}

const loadNodeSwapReasons = async () => {
  await handleOfflineError(async () => {
    if (checkAuthorization({ permission: 'VIEW_SSC_SWAP_NODE_REASONS', entity: '*' })) {
      const swapReasons = await serviceCenterNodeSwapReasonsGet()

      serviceCenterStore.actions.setNodeSwapReasons(swapReasons)
    }
  })
}

const loadNodeRemoveReasons = async () => {
  await handleOfflineError(async () => {
    if (checkAuthorization({ permission: 'VIEW_SSC_REMOVE_NODE_REASONS', entity: '*' })) {
      const removeReasons = await serviceCenterNodeRemoveReasonsGet()

      serviceCenterStore.actions.setNodeRemoveReasons(removeReasons)
    }
  })
}

export const loadStartupData = async () => {
  await Promise.all([loadDevices(), loadLures(), loadNodeSwapReasons(), loadNodeRemoveReasons()])
}

export const loadMapData = async (propertyIds: number[]) => {
  await Promise.all([
    loadPlannedNodes(propertyIds),
    loadActiveNodes(propertyIds),
    loadNodeLogs(propertyIds),
    loadServiceIssues(propertyIds),
  ])
}

// download all the user properties data / static data (help guides) and save them in the store
export const downloadOfflineContents = async (propertyIds: number[]) => {
  // start progress bar
  serviceCenterStore.actions.setOfflineContentsDownloadStatus('3')

  const BATCH_SIZE = 10
  const chunkedPropertyIds = _.chunk(propertyIds, BATCH_SIZE)
  const totalRequests = chunkedPropertyIds.length * 4

  let completedCount = 0

  const onChunkDone = () => {
    completedCount++

    serviceCenterStore.actions.setOfflineContentsDownloadStatus(
      Math.round((completedCount / totalRequests) * 100).toString(),
    )
  }

  const allResults = (
    await Promise.all([
      Promise.allSettled(
        chunkedPropertyIds.map((propertyIds) => loadPlannedNodes(propertyIds).finally(onChunkDone)),
      ),
      Promise.allSettled(
        chunkedPropertyIds.map((propertyIds) => loadActiveNodes(propertyIds).finally(onChunkDone)),
      ),
      Promise.allSettled(
        chunkedPropertyIds.map((propertyIds) => loadNodeLogs(propertyIds).finally(onChunkDone)),
      ),
      Promise.allSettled(
        chunkedPropertyIds.map((propertyIds) => loadServiceIssues(propertyIds).finally(onChunkDone)),
      ),
    ])
  ).flat()

  const rejections = allResults.filter((r) => r.status === 'rejected')

  rejections.forEach((r) => captureFrontendError(r.reason))

  if (rejections.length === allResults.length) {
    serviceCenterStore.actions.setOfflineContentsDownloadStatus('error')
  } else {
    serviceCenterStore.actions.setOfflineContentsDownloadStatus('success')

    serviceCenterStore.actions.setIsOfflineContentBannerOpened(false)
  }
}
